import type { RouteObject } from 'react-router-dom';
import { ReleasedFeature } from '../../release';
import { RequirePermission } from '../auth/guards';
import { TabbedScreens } from '../reports/TabbedScreens';
import { AddUnitPage } from './AddUnitPage';
import { AdjustmentsPage, UnitReassignPage } from './AdjustmentsPages';
import { BulkPage } from './BulkPage';
import { ChangeReasonsPage } from './ChangeReasonsPage';
import { CheckCompatibilityPage } from './CheckCompatibilityPage';
import { EquipmentNameListPage, EquipmentPage } from './EquipmentMastersPages';
import { FullInventoryDashboardPage } from './FullInventoryDashboardPage';
import { InventoryDashboardPage } from './InventoryDashboardPage';
import { ManageInventoryPage } from './ManageInventoryPage';
import { InventorySearchPage } from './InventorySearchPage';
import { ModelsPage } from './ModelsPage';
import { ReportPage } from '../reports/ReportPage';
import {
  INVENTORY_REPORT_KEYS,
  InventoryReportLinks,
  InventoryReportList,
  StockCounters,
} from './ReportLinks';
import { UpdateInventoryPage } from './UpdateInventoryPage';

const guard = (code: string, element: JSX.Element) => (
  <RequirePermission code={code}>{element}</RequirePermission>
);

/** Inventory (PC382) routes, mounted under the app shell. */
export const inventoryRoutes: RouteObject[] = [
  { path: 'inventory', element: guard('PC345', <InventoryDashboardPage />) },
  {
    path: 'inventory/full',
    element: guard(
      'PC345',
      <ReleasedFeature name="inventory-full-dashboard" page>
        <FullInventoryDashboardPage />
      </ReleasedFeature>,
    ),
  },
  { path: 'inventory/manage', element: guard('PC345', <ManageInventoryPage />) },
  {
    path: 'inventory/search',
    element: guard(
      'PC345',
      <ReleasedFeature name="header-inventory-search" page>
        <InventorySearchPage />
      </ReleasedFeature>,
    ),
  },
  { path: 'inventory/units/new', element: guard('PC345', <AddUnitPage />) },
  { path: 'inventory/update', element: guard('PC391', <UpdateInventoryPage />) },
  { path: 'inventory/adjustments', element: guard('PC47', <AdjustmentsPage />) },
  {
    path: 'inventory/adjustments/sim',
    element: guard('PC38', <UnitReassignPage key="sim" kind="sim" />),
  },
  {
    path: 'inventory/adjustments/device',
    element: guard('PC32', <UnitReassignPage key="device" kind="device" />),
  },
  { path: 'inventory/models', element: guard('PC314', <ModelsPage />) },
  { path: 'inventory/change-reasons', element: guard('PC732', <ChangeReasonsPage />) },
  { path: 'inventory/equipment', element: guard('PC679', <EquipmentPage />) },
  {
    path: 'inventory/equipment-types',
    element: guard('PC683', <EquipmentNameListPage key="type" kind="equipmentType" />),
  },
  {
    path: 'inventory/equipment-status-names',
    element: guard('PC727', <EquipmentNameListPage key="status" kind="equipmentStatusName" />),
  },
  {
    path: 'inventory/report',
    element: guard(
      'PC350',
      <>
        <ReleasedFeature name="inventory-report-subreports">
          <InventoryReportList />
        </ReleasedFeature>
        <TabbedScreens
          label="Inventory Report sections"
          tabs={[
            ['inventory-sim-report', 'SIM Inventory Reports'],
            ['inventory-device-report', 'Device Inventory Reports'],
          ]}
        />
      </>,
    ),
  },
  // Inventory Report's ten reports: ReportPage applies their code and switch.
  ...INVENTORY_REPORT_KEYS.map((key) => ({
    path: `inventory/report/${key}`,
    element: <ReportPage reportKey={key} />,
  })),
  {
    path: 'inventory/reporting',
    element: guard(
      'PC19',
      <>
        <TabbedScreens
          label="Inventory Reports"
          tabs={[
            ['stock-complete', 'Complete stock report'],
            ['stock-provisioned', 'Provisioned stock report'],
          ]}
        />
        <ReleasedFeature name="reporting-2">
          <StockCounters />
        </ReleasedFeature>
        <ReleasedFeature name="inventory-report-links">
          <InventoryReportLinks />
        </ReleasedFeature>
      </>,
    ),
  },
  // Reporting's reports of the walk of 2026-10-23: ReportPage applies their code and switch.
  ...[
    'agent-sim-inventory',
    'agent-device-inventory',
    'unassigned-inventory',
    'unassigned-device',
    'sim-summary',
    'unassigned-stock',
    // The walk of 2026-10-27 (release switch `reporting-2`).
    'agent-inventory-report',
    'agent-device-report',
    // The walk of 2026-10-28 (release switch `reporting-3`).
    'sim-batch',
    'device-batch',
    'universal-inventory',
    'vendor-summary',
    'sim-boxes',
    'device-boxes',
    'sim-swap',
  ].map((key) => ({ path: `inventory/reporting/${key}`, element: <ReportPage reportKey={key} /> })),
  {
    path: 'inventory/reporting/check-sim-compatibility',
    element: guard(
      'PC270',
      <ReleasedFeature name="check-sim-compatibility" page>
        <CheckCompatibilityPage />
      </ReleasedFeature>,
    ),
  },
  // The permission depends on the operation, so BulkPage applies RequirePermission itself.
  { path: 'inventory/bulk/:operation', element: <BulkPage /> },
  // Bulk enrollment sits in the orders menu and runs on the same engine.
  { path: 'enrollments/bulk', element: <BulkPage operation="bulk-enrollment" /> },
];
